import styles from './GalleryImage.module.scss';
import ConditionalWrapper from '@components/components/ConditionalWrapper/ConditionalWrapper';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import {
  Button,
  Content,
  Link,
  Heading,
  Image,
  getHeadingTag,
} from '@wienenergiegithub/ui-next';
import { Button as BaseButton } from '@ariakit/react';
import classNames from 'classnames';
import { useMedia } from '@components/hooks';

const Figcaption = ({
  caption,
  content,
  header,
  headingLevel = 3,
  isExtended,
  link,
  variant,
}) => {
  if (isExtended) {
    return (
      <figcaption>
        {header && (
          <Heading
            as={getHeadingTag(headingLevel)}
            className={styles.header}
            size="h3"
          >
            {header}
          </Heading>
        )}
        {content && <Content>{content}</Content>}
        {link?.url && (
          <Button
            className={styles.button}
            render={<Link target={link?.target} to={link.url} />}
            variant={variant}
          >
            {link?.title}
          </Button>
        )}
      </figcaption>
    );
  }

  return caption ? (
    <Content as="figcaption" className={styles.caption} size="regular">
      {caption}
    </Content>
  ) : null;
};

Figcaption.propTypes = {
  caption: PropTypes.string,
  content: PropTypes.string,
  header: PropTypes.string,
  headingLevel: PropTypes.number,
  isExtended: PropTypes.bool,
  link: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    target: PropTypes.string,
  }),
  variant: PropTypes.string,
};

Figcaption.defaultProps = {
  caption: null,
  content: null,
  header: null,
  isExtended: false,
  link: null,
  variant: null,
};

const FigureButton = ({ children, onClick }) => (
  <BaseButton className={styles.figureButton} onClick={onClick}>
    {children}
  </BaseButton>
);

FigureButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
};

const renderWrapperElement = (childen, onClick) => (
  <FigureButton onClick={onClick}>{childen}</FigureButton>
);

const GalleryImage = forwardRef(
  (
    { alt, hasLightbox, headingLevel, onClick, srcSet, src, width, ...props },
    ref,
  ) => {
    const isMobile = useMedia('(max-width: 767px)');

    return (
      <figure>
        <ConditionalWrapper
          condition={hasLightbox}
          wrapper={children => renderWrapperElement(children, onClick)}
        >
          <Image
            alt={alt || ''}
            className={classNames('gallery-image', styles.image)}
            height={isMobile ? 220 : 480}
            ref={ref}
            src={src}
            srcSet={srcSet || undefined}
            width={width}
          />
        </ConditionalWrapper>
        <Figcaption {...props} headingLevel={headingLevel} />
      </figure>
    );
  },
);

GalleryImage.propTypes = {
  alt: PropTypes.string.isRequired,
  caption: PropTypes.string,
  hasLightbox: PropTypes.bool,
  headingLevel: PropTypes.number,
  onClick: PropTypes.func,
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string,
};

GalleryImage.defaultProps = {
  caption: null,
  hasLightbox: false,
  onClick: () => {},
  srcSet: null,
};

GalleryImage.displayName = 'GalleryImage';

export default GalleryImage;
